import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { jwtEnvAxios } from "../../../../../apis";
import { isAxiosError } from "axios";
import { uuid, type AlertTypes } from "djuno-design";

export interface IGitFile {
  path: string;
  index: string;
  working_dir: string;
}
interface IGitStatus {
  not_added: string[];
  conflicted: string[];
  created: string[];
  deleted: string[];
  modified: string[];
  renamed: string[];
  files: Array<IGitFile>;
  staged: string[];
  ahead: number;
  behind: number;
  current: string;
  tracking: null | string;
  detached: boolean;
}

interface IGitConfig {
  name: string;
  email: string;
}

type IGitMessageSection =
  | "status"
  | "init"
  | "add"
  | "config"
  | "commit"
  | "remote"
  | "diff"
  | "push"
  | "pull"
  | "log";
interface IGitMessage {
  id: string;
  type: AlertTypes;
  section: IGitMessageSection;
  message: string;
}

export type GitFlowType =
  | "Commit"
  | "AddAndCommit"
  | "Push"
  | "CommitAndPush"
  | "AddAndCommitAndPush"
  | "CommitAndSync"
  | "AddAndCommitAndSync"
  | "Sync";

const remoteName = "origin";
const branchName = "master";

type AddRemotePayload = {
  username: string;
  token: string;
  repoUrl: string;
};

interface DappGitContextType {
  gitHasInit: boolean;
  gitStatus: IGitStatus | null;
  gitConfig: IGitConfig | null;
  gitHasRemote: boolean;

  gitInitLoading: boolean;
  handleGitInit: () => Promise<void>;

  gitStatusLoading: boolean;
  handleGitStatus: (
    hard?: boolean
  ) => Promise<{ initialized: boolean; status: any | null } | undefined>;

  handleGitLog: () => Promise<void>;

  gitAddLoading: boolean;
  handleGitAdd: (files: string | string[]) => Promise<void>;

  gitCommitLoading: boolean;
  handleGitCommit: (message: string) => Promise<void>;

  gitGetRemoteLoading: boolean;
  handleGitGetRemote: () => Promise<void>;

  gitAddRemoteLoading: boolean;
  handleGitAddRemote: (data: AddRemotePayload) => Promise<void>;

  gitGetConfigLoading: boolean;
  handleGitGetConfig: () => Promise<void>;

  gitAddConfigLoading: boolean;
  handleGitAddConfig: (data: IGitConfig) => Promise<void>;

  diffLoading: boolean;
  handleGitDiff: () => Promise<void>;

  gitPushLoading: boolean;
  handleGitPush: () => Promise<void>;

  gitPullLoading: boolean;
  handleGitPull: (options?: {
    withoutErrorMessage?: boolean | undefined;
  }) => Promise<void>;

  stagedChangedFiles: Array<IGitFile>;
  changedFiles: Array<IGitFile>;

  clearDappStudioGit: () => void;

  messages: Array<IGitMessage>;
  handleClearGitMessageById: (id: string) => void;
}

const DappGitContext = createContext<DappGitContextType | null>(null);

export const DappGitProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [gitHasInit, setGitHasInit] = useState(false);
  const [gitStatus, setGitStatus] = useState<IGitStatus | null>(null);
  const [gitConfig, setGitConfig] = useState<IGitConfig | null>(null);
  const [gitHasRemote, setGitHasRemote] = useState(false);

  const [gitMessages, setGitMessages] = useState<Array<IGitMessage>>([]);

  const handleClearGitMessageById = useCallback((messageId: string) => {
    setGitMessages((oldMessages) => {
      return oldMessages.filter((m) => m.id !== messageId);
    });
  }, []);

  const handleClearGitMessageBySection = useCallback(
    (section: IGitMessageSection) => {
      setGitMessages((oldMessages) => {
        return oldMessages.filter((m) => m.section !== section);
      });
    },
    []
  );

  const handleAddGitMessage = useCallback((data: IGitMessage) => {
    setGitMessages((m) => [...m, data]);
  }, []);

  const [gitStatusLoading, setGitStatusLoading] = useState(false);
  const handleGitStatus = useCallback(
    async (hard: boolean = false) => {
      if (!hard && !gitHasInit) return;
      try {
        setGitStatusLoading(true);
        const response = await jwtEnvAxios("dapp").post(`/git/status`);
        setGitHasInit(true);
        const data =
          import.meta.env.VITE_USE_LOCAL_DAPP === "true"
            ? response.data
            : response.data.Result;

        const initialized = data.initialized || false;
        const status = data.status || null;

        setGitHasInit(initialized);
        setGitStatus(status);
        handleClearGitMessageBySection("status");
        return {
          initialized,
          status,
        };
      } catch (e) {
        if (isAxiosError(e) && e.response?.data.error)
          handleAddGitMessage({
            id: uuid(),
            type: "error",
            section: "status",
            message: e.response?.data.error as string,
          });
      } finally {
        setGitStatusLoading(false);
      }
    },
    [gitHasInit, handleAddGitMessage, handleClearGitMessageBySection]
  );

  const [gitInitLoading, setGitInitLoading] = useState(false);
  const handleGitInit = useCallback(async () => {
    try {
      setGitMessages([]);
      setGitInitLoading(true);
      await jwtEnvAxios("dapp").post(`/git/init`);
      setGitHasInit(true);
      handleGitStatus(true);
      handleClearGitMessageBySection("init");
    } catch (e) {
      console.log("init error:", e);
      if (isAxiosError(e) && e.response?.data.error)
        handleAddGitMessage({
          id: uuid(),
          section: "init",
          type: "error",
          message: e.response?.data.error as string,
        });
    } finally {
      setGitInitLoading(false);
    }
  }, [handleAddGitMessage, handleGitStatus, handleClearGitMessageBySection]);

  const [gitAddLoading, setGitAddLoading] = useState(false);
  const handleGitAdd = useCallback(
    async (files: string | string[]) => {
      try {
        setGitAddLoading(true);
        await jwtEnvAxios("dapp").post(`/git/add`, {
          files,
        });
        handleClearGitMessageBySection("add");
      } catch (e) {
        console.log("add error:", e);
        if (isAxiosError(e) && e.response?.data.error)
          handleAddGitMessage({
            id: uuid(),
            section: "add",
            type: "error",
            message: e.response?.data.error as string,
          });
      } finally {
        setGitAddLoading(false);
      }
    },
    [handleAddGitMessage, handleClearGitMessageBySection]
  );

  const [gitGetConfigLoading, setGitGetConfigLoading] = useState(false);
  const handleGitGetConfig = useCallback(async () => {
    try {
      setGitGetConfigLoading(true);
      const response = await jwtEnvAxios("dapp").get(`/git/config`);
      const configResponse =
        import.meta.env.VITE_USE_LOCAL_DAPP === "true"
          ? response.data
          : response.data.Result;
      const config = configResponse as IGitConfig;
      setGitConfig(config);
      handleClearGitMessageBySection("config");
    } catch (e) {
      console.log("get config error:", e);
      if (isAxiosError(e) && e.response?.data.error)
        handleAddGitMessage({
          id: uuid(),
          section: "config",
          type: "error",
          message: e.response?.data.error as string,
        });
    } finally {
      setGitGetConfigLoading(false);
    }
  }, [handleAddGitMessage, handleClearGitMessageBySection]);

  const [gitAddConfigLoading, setGitAddConfigLoading] = useState(false);
  const handleGitAddConfig = useCallback(
    async (data: IGitConfig) => {
      try {
        setGitAddConfigLoading(true);
        await jwtEnvAxios("dapp").post(`/git/config`, data);
        setGitConfig(data);
        handleClearGitMessageBySection("config");
      } catch (e) {
        console.log("add config error:", e);
        if (isAxiosError(e) && e.response?.data.error)
          handleAddGitMessage({
            id: uuid(),
            section: "config",
            type: "error",
            message: e.response?.data.error as string,
          });
      } finally {
        setGitAddConfigLoading(false);
      }
    },
    [handleAddGitMessage, handleClearGitMessageBySection]
  );

  const [gitCommitLoading, setGitCommitLoading] = useState(false);
  const handleGitCommit = useCallback(
    async (message: string) => {
      try {
        setGitCommitLoading(true);
        await jwtEnvAxios("dapp").post(`/git/commit`, {
          message,
        });
        handleClearGitMessageBySection("commit");
      } catch (e) {
        console.log("commit error:", e);
        if (isAxiosError(e) && e.response?.data.error)
          handleAddGitMessage({
            id: uuid(),
            section: "commit",
            type: "error",
            message: e.response?.data.error as string,
          });
      } finally {
        setGitCommitLoading(false);
      }
    },
    [handleAddGitMessage, handleClearGitMessageBySection]
  );

  const [gitGetRemoteLoading, setGitGetRemoteLoading] = useState(false);
  const handleGitGetRemote = useCallback(async () => {
    try {
      setGitGetRemoteLoading(true);
      const response = await jwtEnvAxios("dapp").get(`/git/remotes`);
      const remotesResponse =
        import.meta.env.VITE_USE_LOCAL_DAPP === "true"
          ? response.data.remotes
          : response.data.Result.remotes;
      const remotes = remotesResponse as Array<{ name: string }>;
      setGitHasRemote(!!remotes.find((r) => r.name === remoteName));
      handleClearGitMessageBySection("remote");
    } catch (e) {
      console.log("get remote error:", e);
      if (isAxiosError(e) && e.response?.data.error)
        handleAddGitMessage({
          id: uuid(),
          section: "remote",
          type: "error",
          message: e.response?.data.error as string,
        });
    } finally {
      setGitGetRemoteLoading(false);
    }
  }, [handleAddGitMessage, handleClearGitMessageBySection]);

  const [gitAddRemoteLoading, setGitAddRemoteLoading] = useState(false);
  const handleGitAddRemote = useCallback(
    async ({ username, token, repoUrl }: AddRemotePayload) => {
      try {
        setGitAddRemoteLoading(true);
        await jwtEnvAxios("dapp").post(`/git/add-remote`, {
          remoteName,
          username,
          token,
          repoUrl,
        });
        setGitHasRemote(true);
        handleGitStatus();
        handleClearGitMessageBySection("remote");
      } catch (e) {
        console.log("add remote error:", e);
        if (isAxiosError(e) && e.response?.data.error)
          handleAddGitMessage({
            id: uuid(),
            section: "remote",
            type: "error",
            message: e.response?.data.error as string,
          });
      } finally {
        setGitAddRemoteLoading(false);
      }
    },
    [handleAddGitMessage, handleClearGitMessageBySection, handleGitStatus]
  );

  const [diffLoading, setDiffLoading] = useState(false);
  const handleGitDiff = useCallback(async () => {
    try {
      setDiffLoading(true);
      await jwtEnvAxios("dapp").post(`/git/commit-differences`, {
        remote: remoteName,
        branch: branchName,
      });
      handleClearGitMessageBySection("diff");
    } catch (e) {
      console.log("diff error:", e);
      if (isAxiosError(e) && e.response?.data.error)
        handleAddGitMessage({
          id: uuid(),
          section: "diff",
          type: "error",
          message: e.response?.data.error as string,
        });
    } finally {
      setDiffLoading(false);
    }
  }, [handleAddGitMessage, handleClearGitMessageBySection]);

  const [gitPushLoading, setGitPushLoading] = useState(false);
  const handleGitPush = useCallback(async () => {
    try {
      setGitPushLoading(true);
      await jwtEnvAxios("dapp").post(`/git/push`, {
        remote: remoteName,
        branch: branchName,
      });
      handleClearGitMessageBySection("push");
    } catch (e) {
      console.log("push error:", e);
      if (isAxiosError(e) && e.response?.data.error)
        handleAddGitMessage({
          id: uuid(),
          section: "push",
          type: "error",
          message: e.response?.data.error as string,
        });
    } finally {
      setGitPushLoading(false);
    }
  }, [handleAddGitMessage, handleClearGitMessageBySection]);

  const [gitPullLoading, setGitPullLoading] = useState(false);
  const handleGitPull = useCallback(
    async (
      options: { withoutErrorMessage?: boolean | undefined } | undefined
    ) => {
      try {
        setGitPullLoading(true);
        await jwtEnvAxios("dapp").post(`/git/pull`, {
          remote: remoteName,
          branch: branchName,
        });
        handleClearGitMessageBySection("pull");
      } catch (e) {
        console.log("pull error:", e);
        if (
          isAxiosError(e) &&
          e.response?.data.error &&
          !options?.withoutErrorMessage
        )
          handleAddGitMessage({
            id: uuid(),
            section: "pull",
            type: "error",
            message: e.response?.data.error as string,
          });
      } finally {
        setGitPullLoading(false);
      }
    },
    [handleAddGitMessage, handleClearGitMessageBySection]
  );

  const handleGitLog = useCallback(async () => {
    try {
      await jwtEnvAxios("dapp").post(`/git/log`);
      handleClearGitMessageBySection("log");
    } catch (e) {
      console.log("log error:", e);
      if (isAxiosError(e) && e.response?.data.error)
        handleAddGitMessage({
          id: uuid(),
          section: "log",
          type: "error",
          message: e.response?.data.error as string,
        });
    }
  }, [handleAddGitMessage, handleClearGitMessageBySection]);

  // calc staged changes files
  const stagedChangedFiles = useMemo(() => {
    const files: Array<IGitFile> = [];
    if (!gitStatus || gitStatus.files.length === 0) return [];

    for (const file of gitStatus.files) {
      if (
        file.index === "A" ||
        (file.working_dir === "M" && file.index !== " ")
      ) {
        files.push(file);
      }
    }
    return files;
  }, [gitStatus]);

  // calc changed files
  const changedFiles = useMemo(() => {
    const files: Array<IGitFile> = [];
    if (!gitStatus || gitStatus.files.length === 0) return [];

    for (const file of gitStatus.files) {
      if (file.index === "?" || file.working_dir === "M") files.push(file);
    }
    return files;
  }, [gitStatus]);

  const clearDappStudioGit = useCallback(() => {
    setGitHasInit(false);
    setGitStatus(null);
    setGitConfig(null);
    setGitHasRemote(false);
    setGitMessages([]);
  }, []);

  return (
    <DappGitContext.Provider
      value={{
        gitHasInit,
        gitStatus,
        gitConfig,
        gitHasRemote,

        gitInitLoading,
        handleGitInit,

        gitStatusLoading,
        handleGitStatus,

        handleGitLog,

        gitAddLoading,
        handleGitAdd,

        gitCommitLoading,
        handleGitCommit,

        gitGetRemoteLoading,
        handleGitGetRemote,

        gitAddRemoteLoading,
        handleGitAddRemote,

        gitGetConfigLoading,
        handleGitGetConfig,

        gitAddConfigLoading,
        handleGitAddConfig,

        diffLoading,
        handleGitDiff,

        gitPushLoading,
        handleGitPush,

        gitPullLoading,
        handleGitPull,

        stagedChangedFiles,
        changedFiles,

        clearDappStudioGit,

        messages: gitMessages,
        handleClearGitMessageById,
      }}
    >
      {children}
    </DappGitContext.Provider>
  );
};

export const useDappGit = () => {
  const context = useContext(DappGitContext);
  if (!context)
    throw new Error("useDappGit must be used within DappGitProvider");
  return context;
};
